<template>
  <div class="pnl-performance">
    <!-- Summary card -->
    <div class="pnl-card pnl-summary mt-2">
      <div class="pnl-summary-left">
        <div class="pnl-label">
          Performance
          <span class="pnl-label-range">· {{ rangeLabel }}</span>
        </div>
        <spinner v-if="loading" />
        <template v-else-if="rangeError">
          <div class="pnl-total pnl-total-muted">&mdash;</div>
          <div class="pnl-sub">Couldn't load this range right now</div>
        </template>
        <template v-else>
          <div
            class="pnl-total"
            :class="summary.total >= 0 ? 'positive' : 'negative'"
          >
            {{ formatSignedMoney(summary.total) }}
          </div>
          <div class="pnl-sub">
            {{ summary.settledBets.toLocaleString() }} settled
            {{ summary.settledBets === 1 ? 'bet' : 'bets' }}
          </div>
        </template>
      </div>
      <div class="pnl-range-pills">
        <button
          v-for="option in rangeOptions"
          :key="option.value"
          class="pnl-pill"
          :class="{ active: range === option.value }"
          @click="setRange(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <!-- Chart card -->
    <div class="pnl-card mt-2">
      <div class="pnl-card-title">Profit Over Time</div>
      <spinner v-if="loading" />
      <div v-else-if="rangeError" class="pnl-empty text-center py-4">
        Couldn't load this range right now &mdash; try a shorter window.
      </div>
      <div
        v-else-if="chartPoints.length < 2"
        class="pnl-empty text-center py-4"
      >
        Not enough settled bets in this range to chart yet.
      </div>
      <div
        v-else
        ref="chartWrap"
        class="pnl-chart-wrap"
        @mousemove="onChartHover"
        @mouseleave="hoverIndex = null"
      >
        <svg
          class="pnl-chart"
          :viewBox="`0 0 ${chartW} ${chartH}`"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="pnlFillGreen" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#009f25" stop-opacity="0.35" />
              <stop offset="100%" stop-color="#009f25" stop-opacity="0.02" />
            </linearGradient>
            <linearGradient id="pnlFillRed" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#dc3545" stop-opacity="0.02" />
              <stop offset="100%" stop-color="#dc3545" stop-opacity="0.35" />
            </linearGradient>
          </defs>
          <!-- zero baseline -->
          <line
            :x1="0"
            :x2="chartW"
            :y1="zeroY"
            :y2="zeroY"
            class="pnl-zero-line"
            vector-effect="non-scaling-stroke"
          />
          <path :d="areaPath" :fill="lineIsPositive ? 'url(#pnlFillGreen)' : 'url(#pnlFillRed)'" />
          <path
            :d="linePath"
            fill="none"
            :stroke="lineIsPositive ? '#009f25' : '#dc3545'"
            stroke-width="2"
            vector-effect="non-scaling-stroke"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
          <!-- hover crosshair -->
          <g v-if="hoverPoint">
            <line
              :x1="hoverPoint.x"
              :x2="hoverPoint.x"
              :y1="0"
              :y2="chartH"
              class="pnl-crosshair"
              vector-effect="non-scaling-stroke"
            />
            <circle
              :cx="hoverPoint.x"
              :cy="hoverPoint.y"
              r="3.5"
              :fill="lineIsPositive ? '#009f25' : '#dc3545'"
            />
          </g>
        </svg>
        <div
          v-if="hoverPoint"
          class="pnl-tooltip"
          :style="tooltipStyle"
        >
          <div class="pnl-tooltip-date">{{ hoverPoint.dateLabel }}</div>
          <div
            class="pnl-tooltip-value"
            :class="hoverPoint.cumulative >= 0 ? 'positive' : 'negative'"
          >
            {{ formatSignedMoney(hoverPoint.cumulative) }}
          </div>
          <div class="pnl-tooltip-day">
            {{ bucketWord }}: <span :class="hoverPoint.dayPnl >= 0 ? 'positive' : 'negative'">{{
              formatSignedMoney(hoverPoint.dayPnl)
            }}</span>
          </div>
        </div>
        <div class="pnl-axis">
          <span>{{ chartPoints[0]?.dateLabel }}</span>
          <span>{{ chartPoints[chartPoints.length - 1]?.dateLabel }}</span>
        </div>
      </div>
    </div>

    <!-- Breakdown card -->
    <div class="pnl-card mt-2 mb-3">
      <div class="pnl-card-title">By Sport</div>
      <spinner v-if="loading" />
      <div v-else-if="rangeError" class="pnl-empty text-center py-4">
        Couldn't load this range right now.
      </div>
      <div
        v-else-if="summary.byLeague.length === 0"
        class="pnl-empty text-center py-4"
      >
        No settled bets in this range.
      </div>
      <div v-else class="pnl-league-list">
        <div
          v-for="row in summary.byLeague"
          :key="row.league"
          class="pnl-league-row"
        >
          <div class="pnl-league-icon">
            <font-awesome-icon :icon="leagueIcon(row)" />
          </div>
          <div class="pnl-league-name">
            {{ row.league }}
            <span class="pnl-league-bets">
              {{ row.settledBets.toLocaleString() }}
              {{ row.settledBets === 1 ? 'bet' : 'bets' }}
            </span>
          </div>
          <div class="pnl-league-bar-track">
            <div
              class="pnl-league-bar"
              :class="row.pnl >= 0 ? 'bar-positive' : 'bar-negative'"
              :style="{ width: barWidth(row.pnl) }"
            />
          </div>
          <div
            class="pnl-league-amount"
            :class="row.pnl >= 0 ? 'positive' : 'negative'"
          >
            {{ formatSignedMoney(row.pnl) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { getFaSportIcon } from '@/core/getIcon.js';
import { useAPI } from '@/composables/useAPI';
import Spinner from '@/components/global/Spinner.vue';
import { buildChartPoints, formatSignedMoney } from '@/core/pnlUtils';

const { get: getPnlSummary } = useAPI('user/getPnlSummary');

const rangeOptions = [
  { value: '1d', label: '1D' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: 'ytd', label: 'This Year' },
  { value: 'all', label: 'All' },
];

const range = ref('30d');
const loading = ref(true);
// True when the current range's fetch failed (e.g. an enormous all-time
// ledger hitting the server's query-time ceiling). Shown as an honest
// error state instead of leaving another range's numbers on screen.
const rangeError = ref(false);
const hoverIndex = ref(null);
const chartWrap = ref(null);

const emptySummary = () => ({
  total: 0,
  settledBets: 0,
  daily: [],
  byLeague: [],
  granularity: 'day',
  effectiveRange: null,
});
const summary = ref(emptySummary());
// One fetch per range per visit; switching back is instant.
const cache = {};
// Monotonic id so a slow in-flight response can't overwrite a newer
// selection (same guard the parent view uses for fetchWeeklyBets).
let requestCounter = 0;

const chartW = 640;
const chartH = 200;

const RANGE_LABELS = {
  '1d': 'Last 24 Hours',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  ytd: 'This Year',
  all: 'All Time',
};

const rangeLabel = computed(
  () => RANGE_LABELS[summary.value.effectiveRange || range.value] || RANGE_LABELS.all,
);

async function fetchRange(value) {
  if (cache[value]) {
    // Invalidate any in-flight request so its late response is discarded.
    requestCounter += 1;
    summary.value = cache[value];
    loading.value = false;
    rangeError.value = false;
    return;
  }
  const requestId = ++requestCounter;
  loading.value = true;
  rangeError.value = false;
  try {
    const data = await getPnlSummary({ range: value });
    const clean = data
      ? {
          total: data.total || 0,
          settledBets: data.settledBets || 0,
          daily: data.daily || [],
          byLeague: data.byLeague || [],
          granularity: data.granularity || 'day',
          // The server echoes the range it actually used. An older core-api
          // that doesn't know a range serves all-time instead, and the label
          // must follow the data rather than the button we pressed.
          effectiveRange: data.range || value,
        }
      : emptySummary();
    // Filling the cache is safe even for a superseded request; only the
    // still-current one may touch what's on screen.
    cache[value] = clean;
    if (requestId === requestCounter) summary.value = clean;
  } catch {
    // Failed (timeout on a huge ledger, network, …). Clear the stale range's
    // numbers and show the error state; not cached, so re-selecting retries.
    if (requestId === requestCounter) {
      summary.value = emptySummary();
      rangeError.value = true;
    }
  } finally {
    if (requestId === requestCounter) loading.value = false;
  }
}

function setRange(value) {
  if (range.value === value) return;
  range.value = value;
  hoverIndex.value = null;
  fetchRange(value);
}

onMounted(() => fetchRange(range.value));

// ── Chart geometry (shared helpers in @/core/pnlUtils) ─────────────────

const chartPoints = computed(() =>
  buildChartPoints(summary.value.daily, summary.value.granularity, {
    withYear: (summary.value.effectiveRange || range.value) === 'all',
  }),
);

const yDomain = computed(() => {
  const values = chartPoints.value.map((p) => p.cumulative);
  let min = Math.min(0, ...values);
  let max = Math.max(0, ...values);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const pad = (max - min) * 0.08;
  return { min: min - pad, max: max + pad };
});

function yFor(value) {
  const { min, max } = yDomain.value;
  return chartH - ((value - min) / (max - min)) * chartH;
}

function xFor(index) {
  const n = chartPoints.value.length;
  return n <= 1 ? 0 : (index / (n - 1)) * chartW;
}

const zeroY = computed(() => yFor(0));

const linePath = computed(() =>
  chartPoints.value
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(2)},${yFor(p.cumulative).toFixed(2)}`,
    )
    .join(''),
);

// Area closes against the zero baseline (not the chart floor) so red
// drawdowns hang below the line and green profit sits above it.
const areaPath = computed(() => {
  if (!chartPoints.value.length) return '';
  const z = zeroY.value.toFixed(2);
  return `M0,${z}${chartPoints.value
    .map((p, i) => `L${xFor(i).toFixed(2)},${yFor(p.cumulative).toFixed(2)}`)
    .join('')}L${chartW},${z}Z`;
});

const bucketWord = computed(() =>
  summary.value.granularity === 'hour' ? 'Hour' : 'Day',
);

const lineIsPositive = computed(() => {
  const points = chartPoints.value;
  return !points.length || points[points.length - 1].cumulative >= 0;
});

const hoverPoint = computed(() => {
  if (hoverIndex.value === null) return null;
  const p = chartPoints.value[hoverIndex.value];
  if (!p) return null;
  return { ...p, x: xFor(hoverIndex.value), y: yFor(p.cumulative) };
});

const tooltipStyle = computed(() => {
  if (!hoverPoint.value) return {};
  const pct = (hoverPoint.value.x / chartW) * 100;
  // Flip the tooltip to the other side of the crosshair near the edges.
  return pct > 65
    ? { right: `${100 - pct + 2}%` }
    : { left: `${pct + 2}%` };
});

function onChartHover(event) {
  const el = chartWrap.value;
  if (!el || chartPoints.value.length < 2) return;
  const rect = el.getBoundingClientRect();
  const ratio = (event.clientX - rect.left) / rect.width;
  const index = Math.round(ratio * (chartPoints.value.length - 1));
  hoverIndex.value = Math.max(0, Math.min(chartPoints.value.length - 1, index));
}

// ── Breakdown helpers ───────────────────────────────────────────────────

const maxAbsLeaguePnl = computed(() =>
  Math.max(1, ...summary.value.byLeague.map((row) => Math.abs(row.pnl))),
);

function barWidth(pnl) {
  return `${(Math.abs(pnl) / maxAbsLeaguePnl.value) * 100}%`;
}

function leagueIcon(row) {
  return getFaSportIcon({
    sport: row.sport,
    name: row.league,
    league: row.league,
  });
}

</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.pnl-card {
  background: main.$grey-500;
  border-radius: 10px;
  padding: 1rem 1.25rem;
}

/* The global Spinner forces min-height 535px under sm and my-5 margins,
   several times these cards' loaded height, which would balloon then
   collapse each card on every uncached range switch. Constrain it to the
   card it's loading. */
.pnl-card :deep(.spinner) {
  min-height: 0;
  margin: 1.5rem auto !important;
}

/* The summary card carries BOTH classes, so this must out-specify the rule
   above rather than rely on source order: its spinner sits inline with the
   header text and wants the tighter margin. */
.pnl-card.pnl-summary :deep(.spinner) {
  margin: 0.5rem auto !important;
}

.pnl-card-title {
  color: main.$color-primary_01;
  font-size: main.$font-size-sm;
  font-weight: main.$font-weight-semibold;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.75rem;
}

.pnl-empty {
  color: main.$text-gray;
  font-size: main.$font-size-sm;
}

/* ── Summary ── */
.pnl-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.pnl-label {
  color: main.$color-primary_01;
  font-size: main.$font-size-sm;
  font-weight: main.$font-weight-semibold;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.pnl-label-range {
  color: main.$text-gray;
  text-transform: none;
  letter-spacing: normal;
  font-weight: normal;
}

.pnl-total {
  font-size: 2.25rem;
  font-weight: bold;
  line-height: 1.15;
}

.pnl-sub {
  color: main.$text-gray;
  font-size: main.$font-size-sm;
}

.pnl-total-muted {
  color: main.$text-gray;
}

.pnl-range-pills {
  display: flex;
  gap: 0.4rem;
}

.pnl-pill {
  border: 1px solid main.$blue-1400;
  background: main.$white_03;
  color: main.$color-primary_01;
  border-radius: 999px;
  font-size: main.$font-size-sm;
  padding: 0.3rem 0.9rem;
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:focus {
    outline: none;
  }

  &.active {
    box-shadow:
      0 0 0 1px rgba(120, 152, 255, 0.45),
      0 0 14px rgba(120, 152, 255, 0.5);
    font-weight: main.$font-weight-semibold;
  }
}

/* ── Chart ── */
.pnl-chart-wrap {
  position: relative;
}

.pnl-chart {
  display: block;
  width: 100%;
  height: 200px;
}

.pnl-zero-line {
  stroke: main.$text-gray;
  stroke-width: 1;
  stroke-dasharray: 4 4;
  opacity: 0.5;
}

.pnl-crosshair {
  stroke: main.$text-gray;
  stroke-width: 1;
  opacity: 0.7;
}

.pnl-axis {
  display: flex;
  justify-content: space-between;
  color: main.$text-gray;
  font-size: main.$font-size-xs;
  margin-top: 0.25rem;
}

.pnl-tooltip {
  position: absolute;
  top: 8px;
  background: main.$grey-500;
  border: 1px solid main.$blue-1400;
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  font-size: main.$font-size-xs;
  pointer-events: none;
  white-space: nowrap;
  box-shadow: 0 1px 6px 0 main.$blue-1200;
  z-index: 2;
}

.pnl-tooltip-date {
  color: main.$text-gray;
}

.pnl-tooltip-value {
  font-size: main.$font-size-sm;
  font-weight: bold;
}

.pnl-tooltip-day {
  color: main.$text-gray;
}

/* ── Breakdown ── */
.pnl-league-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.pnl-league-row {
  display: grid;
  grid-template-columns: 28px minmax(120px, 200px) 1fr 110px;
  align-items: center;
  gap: 0.75rem;
  padding: 0.35rem 0;
}

.pnl-league-icon {
  color: main.$color-primary_01;
  text-align: center;
}

.pnl-league-name {
  color: main.$color-primary_01;
  font-size: main.$font-size-sm;
  font-weight: main.$font-weight-semibold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pnl-league-bets {
  display: block;
  color: main.$text-gray;
  font-size: main.$font-size-xs;
  font-weight: normal;
}

.pnl-league-bar-track {
  height: 8px;
  border-radius: 999px;
  background: main.$white_03;
  overflow: hidden;
}

.pnl-league-bar {
  height: 100%;
  border-radius: 999px;
  min-width: 2px;
}

.bar-positive {
  background: main.$green-700;
}

.bar-negative {
  background: main.$red-500;
}

.pnl-league-amount {
  text-align: right;
  font-size: main.$font-size-sm;
}

.positive {
  color: main.$green-700;
  font-weight: bold;
}

.negative {
  color: main.$red-500;
  font-weight: bold;
}

/* Mobile: the summary card stacks and the range pills spread into a
   full-width five-up grid — side-by-side they crowd the total. */
@media screen and (max-width: (map.get(main.$grid-breakpoints, md) - 0.02)) {
  .pnl-summary {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .pnl-range-pills {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.35rem;
  }

  .pnl-pill {
    padding: 0.45rem 0.15rem;
    font-size: clamp(0.65rem, 3vw, main.$font-size-sm);
    text-align: center;
    white-space: nowrap;
  }

  .pnl-league-row {
    grid-template-columns: 24px minmax(70px, auto) 1fr 84px;
    gap: 0.5rem;
  }
}
</style>
